/* eslint-disable @typescript-eslint/member-ordering */
/* eslint-disable @typescript-eslint/no-inferrable-types */
/* eslint-disable @typescript-eslint/naming-convention */
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { EventEmitter, Injectable } from '@angular/core';
import { AuthenticationService } from './authentication.service';
import { environment } from 'src/environments/environment';
import { EmployeesMessages } from '../models/empleados';
import { HttpResponseAPI } from '../models/httpResponse';
import { BehaviorSubject, Observable, concat, from } from 'rxjs';
import { concatMap } from 'rxjs/operators';

@Injectable({
    providedIn: 'root'
})
export class EmployeesService {

    // #region Propiedades públicas
    private mensajes: EmployeesMessages[] = [];
    m$: BehaviorSubject<EmployeesMessages[]> = new BehaviorSubject<EmployeesMessages[]>(this.mensajes);
    mensajes$: Observable<EmployeesMessages[]> = this.m$.asObservable();

    private mensajesNoLeidos: EmployeesMessages[] = [];
    mnl$: BehaviorSubject<EmployeesMessages[]> = new BehaviorSubject<EmployeesMessages[]>(this.mensajesNoLeidos);
    mensajesNoLeidos$: Observable<EmployeesMessages[]> = this.mnl$.asObservable();

    private mensajesObligatorios: EmployeesMessages[] = [];
    mo$: BehaviorSubject<EmployeesMessages[]> = new BehaviorSubject<EmployeesMessages[]>(this.mensajesObligatorios);
    mensajesObligatorios$: Observable<EmployeesMessages[]> = this.mo$.asObservable();

    loading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    // #endregion

    // #region Eventos públicos
    eventoTieneMensajesObligatorio: EventEmitter<boolean> = new EventEmitter<boolean>();
    eventoSinMensajesObligatorio: EventEmitter<boolean> = new EventEmitter<boolean>();
    // #endregion

    private NombreServicio: string = 'employees';

    constructor(
        private http: HttpClient,
        private authenticationService: AuthenticationService) { }

    // #region Métodos del servicio relacionados directamente con la API

    /**
     * Obtiene la lista de los mensajes asociados con el empleado.
     */
    getMensajes(): void {
        this.loading$.next(true);
        const cargarToken = from(this.authenticationService.loadToken());

        cargarToken
            .pipe(
                concatMap(() => {
                    const headers = new HttpHeaders({
                        'Content-Type': 'application/json',
                        Authorization: 'Bearer ' + this.authenticationService.token
                    });
                    return this.http.get(`${environment.apiUrl}/${this.NombreServicio}/messages`, { headers });
                })
            )
            .subscribe({
                next: (resultado: HttpResponseAPI) => {
                    // En la lista de mensajes estarán los mensajes no oblitatorios y los obligatorios leídos
                    this.mensajes = resultado.data.filter(m => !m.lecturaObligatoria || m.leido);
                    this.m$.next(this.mensajes);

                    // Filtramos los mensajes no leídos
                    this.mensajesNoLeidos = resultado.data.filter(m => !m.leido);
                    this.mnl$.next(this.mensajesNoLeidos);

                    // Filtramos los mensajes obligatorios
                    this.mensajesObligatorios = resultado.data.filter(m => m.lecturaObligatoria && !m.leido);
                    this.mo$.next(this.mensajesObligatorios);

                    if (this.mensajesObligatorios.length > 0) {
                        this.eventoTieneMensajesObligatorio.emit(true);
                    } else if (this.mensajesObligatorios.length === 0) {
                        this.eventoSinMensajesObligatorio.emit(true);
                    }
                }
            })
            .add(() => {
                this.loading$.next(false);
            });
    }

    /**
     * Obtiene la firma del empleado.
     * @returns Un Observable que emite la firma del empleado como una cadena de texto.
     */
    getSignature(): Observable<string> {
        this.loading$.next(true);

        return new Observable<string>((observer) => {
            const cargarToken = from(this.authenticationService.loadToken());

            cargarToken
                .pipe(
                    concatMap(() => {
                        const headers = new HttpHeaders({
                            'Content-Type': 'application/json',
                            'Authorization': 'Bearer ' + this.authenticationService.token
                        });
                        return this.http.get<HttpResponseAPI>(`${environment.apiUrl}/${this.NombreServicio}/signature`, { headers });
                    })
                )
                .subscribe({
                    next: (resultado: HttpResponseAPI) => {
                        observer.next(resultado.data);
                        observer.complete();
                    }
                })
                .add(() => this.loading$.next(false));
        });
    }

    /**
     * Guarda la firma del empleado.
     * @param signature La firma a guardar.
     * @returns Un Observable que emite un valor booleano indicando si se guardó la firma correctamente.
     */
    saveSignature(signature: string): Observable<boolean> {
        this.loading$.next(true);

        return new Observable<boolean>((observer) => {
            const cargarToken = from(this.authenticationService.loadToken());

            cargarToken
                .pipe(
                    concatMap(() => {
                        const headers = new HttpHeaders({
                            'Content-Type': 'application/json',
                            'Authorization': 'Bearer ' + this.authenticationService.token
                        });
                        return this.http.post<HttpResponseAPI>(`${environment.apiUrl}/${this.NombreServicio}/signature`, { signature }, { headers });
                    })
                )
                .subscribe({
                    next: (resultado: HttpResponseAPI) => {
                        observer.next(resultado.data);
                        observer.complete();
                    }
                })
                .add(() => this.loading$.next(false));
        });
    }

    /**
     * Marca el mensaje como leído.
     *
     * @param mensaje Mensaje a marcar como leído.
     */
    setMensajeLeido(mensaje: EmployeesMessages): void {
        this.loading$.next(true);
        const cargarToken = from(this.authenticationService.loadToken());

        cargarToken
            .pipe(
                concatMap(() => {
                    const headers = new HttpHeaders({
                        'Content-Type': 'application/json',
                        Authorization: 'Bearer ' + this.authenticationService.token
                    });

                    return this.http.get(`${environment.apiUrl}/${this.NombreServicio}/messages/read/${mensaje.messageId}`,
                        { headers });
                })
            )
            .subscribe({
                next: (resultado: HttpResponseAPI) => {
                    this.getMensajes();
                }
            })
            .add(() => {
                this.loading$.next(false);
            });
    }

    // #endregion

    // #region Métodos de gestión interna
    /**
     * Obtiene el número de mensajes no leídos.
     * @returns El número de mensajes no leídos.
     */
    getMensajesNoLeidos(): number {
        return this.mensajes != null && this.mensajes.length > 0 ? this.mensajes.filter(m => !m.leido).length : 0;
    }
    // #endregion
}
